import { useEffect, useState, useCallback } from 'react'
import {
  apiAdminGetTelegramSettings,
  apiAdminUpdateTelegramSettings,
  apiAdminTestTelegram,
  apiAdminGetDrawAutoOpen,
  apiAdminSetDrawAutoOpen,
  ApiRequestError,
  type TelegramSettingsMasked,
  type UpdateTelegramBody,
} from '@/lib/api'
import type { OtpConfirmation } from '@nexbit/types'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Send, Save, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2, Bell, CalendarClock,
} from 'lucide-react'

/** A labelled on/off pill — no Switch primitive in this kit, so build a small one. */
function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
      aria-label={label}
    >
      <span className={cn('h-5 w-5 rounded-full bg-background shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  )
}

/**
 * Whether the worker opens each lottery's next งวด on schedule.
 *
 * Off, a งวด only exists once an admin presses "เปิดงวดใหม่" — so a lottery whose
 * result is entered late takes no bets for the following round either. On, the worker
 * opens the next slot as soon as the current one stops taking bets, and entering
 * results is the only manual step left.
 */
function DrawAutoOpenCard() {
  const [enabled, setEnabled] = useState(false)
  const [delayHours, setDelayHours] = useState(3)
  /** Last saved value — the dirty check for the บันทึก button next to the field. */
  const [savedDelay, setSavedDelay] = useState(3)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const s = await apiAdminGetDrawAutoOpen()
      setEnabled(s.enabled)
      setDelayHours(s.delayHoursAfterDraw)
      setSavedDelay(s.delayHoursAfterDraw)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดการตั้งค่าไม่สำเร็จ')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  async function save(next: { enabled: boolean; delayHoursAfterDraw: number }) {
    setSaving(true); setNotice(null)
    const previous = { enabled, delayHours }
    setEnabled(next.enabled)
    setDelayHours(next.delayHoursAfterDraw)
    try {
      const saved = await apiAdminSetDrawAutoOpen(next)
      setEnabled(saved.enabled)
      setDelayHours(saved.delayHoursAfterDraw)
      setSavedDelay(saved.delayHoursAfterDraw)
      setNotice({
        kind: 'ok',
        text: saved.enabled
          ? `เปิดงวดถัดไปอัตโนมัติ หลังออกผลแล้ว ${saved.delayHoursAfterDraw} ชั่วโมง`
          : 'ปิดแล้ว — ต้องเปิดงวดเองจากหน้าหวย',
      })
    } catch (err) {
      setEnabled(previous.enabled)
      setDelayHours(previous.delayHours)
      setNotice({ kind: 'err', text: err instanceof ApiRequestError ? err.message : 'บันทึกไม่สำเร็จ' })
    } finally { setSaving(false) }
  }

  // Saved on toggle rather than behind a button: it is a single switch, and a
  // half-flipped state that needs confirming reads as broken.
  const toggle = (next: boolean) => save({ enabled: next, delayHoursAfterDraw: delayHours })

  return (
    <section className="mt-5 border border-border rounded-[var(--radius)] bg-card shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-primary" />
        <span className="font-semibold text-sm">เปิดงวดถัดไปอัตโนมัติ</span>
        {!loading && !error && (
          <span className={cn('ml-auto text-xs font-medium rounded-full px-2 py-0.5',
            enabled ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground')}>
            {enabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-4 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="p-6 flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>ลองใหม่</Button>
        </div>
      ) : (
        <div className="p-4 space-y-5">
          <p className="text-xs text-muted-foreground leading-relaxed">
            ระบบจะเปิดงวดถัดไปตาม <strong>ตารางออกผลของแต่ละหวย</strong> ให้เอง
            โดยรอหลังเวลาออกผลของงวดปัจจุบันตามที่ตั้งไว้ด้านล่าง
            <br />
            ถ้าปิดสวิตช์นี้ ต้องกด &ldquo;เปิดงวดใหม่&rdquo; เองทุกงวดจากหน้าหวย
          </p>

          <div className="flex items-center justify-between gap-4 py-1">
            <div>
              <Label className="text-sm">เปิดงวดถัดไปอัตโนมัติ</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                ใช้กับหวยที่เปิดใช้งานและตั้งตารางออกผลไว้ (ไม่รวมแบบกำหนดเอง)
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              <Toggle
                checked={enabled}
                onChange={(v) => { if (!saving) void toggle(v) }}
                label="เปิดงวดถัดไปอัตโนมัติ"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4 border-t border-border pt-4">
            <div className="min-w-0">
              <Label htmlFor="auto-open-delay" className="text-sm">รอหลังเวลาออกผล</Label>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                นับจาก<strong>เวลาออกผล</strong>ของงวดปัจจุบัน ไม่ใช่เวลาปิดรับแทง —
                มีเวลากรอกผลก่อนงวดใหม่จะขึ้น ไม่ให้หวยเดียวกันโผล่สองงวดพร้อมกัน
                <br />
                ตั้ง 0 = เปิดงวดใหม่ทันทีที่งวดเก่าปิดรับแทง (แบบเดิม)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="auto-open-delay"
                type="number"
                min={0}
                max={72}
                value={delayHours}
                onChange={(e) => setDelayHours(Math.max(0, Math.min(72, Number(e.target.value) || 0)))}
                className="h-9 w-20 font-mono"
              />
              <span className="text-sm text-muted-foreground">ชั่วโมง</span>
              {delayHours !== savedDelay && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={saving}
                  onClick={() => void save({ enabled, delayHoursAfterDraw: delayHours })}
                >
                  <Save className="h-3.5 w-3.5" />
                  บันทึก
                </Button>
              )}
            </div>
          </div>

          {notice && (
            <div
              role="status"
              className={cn('flex items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-xs',
                notice.kind === 'ok' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive')}
            >
              {notice.kind === 'ok' ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              {notice.text}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

export default function AdminSettingsPage() {
  const [data, setData] = useState<TelegramSettingsMasked | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // form state
  const [enabled, setEnabled] = useState(false)
  const [chatId, setChatId] = useState('')
  const [botToken, setBotToken] = useState('') // empty = keep stored token
  const [showToken, setShowToken] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  // Snapshotted when the operator asks for a code, not read live at confirm time: the
  // code is bound to these exact values, so what is submitted must be what was approved.
  const [pending, setPending] = useState<UpdateTelegramBody | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const s = await apiAdminGetTelegramSettings()
      setData(s); setEnabled(s.enabled); setChatId(s.chatId); setBotToken('')
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดการตั้งค่าไม่สำเร็จ')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  /**
   * This is the one setting that decides where every other code is delivered, so it is
   * itself behind a code sent to the group it currently points at — repointing it
   * requires still having access to the old group.
   */
  function askToSave() {
    setNotice(null)
    setPending({
      enabled, chatId: chatId.trim(),
      ...(botToken.trim() ? { botToken: botToken.trim() } : {}),
    })
  }

  async function save(body: UpdateTelegramBody, otp: OtpConfirmation) {
    setSaving(true)
    try {
      const s = await apiAdminUpdateTelegramSettings(body, otp)
      setData(s); setBotToken(''); setShowToken(false)
      setNotice({ kind: 'ok', text: 'บันทึกการตั้งค่าแล้ว' })
    } finally { setSaving(false) }
  }

  async function test() {
    setTesting(true); setNotice(null)
    try {
      await apiAdminTestTelegram()
      setNotice({ kind: 'ok', text: 'ส่งข้อความทดสอบเข้ากลุ่มแล้ว — เช็คใน Telegram' })
    } catch (err) {
      setNotice({ kind: 'err', text: err instanceof ApiRequestError ? err.message : 'ส่งทดสอบไม่สำเร็จ' })
    } finally { setTesting(false) }
  }

  const dirty = data ? (enabled !== data.enabled || chatId.trim() !== data.chatId || botToken.trim() !== '') : false
  const tokenPlaceholder = data?.hasToken ? `บันทึกไว้แล้ว •••• ${data.tokenPreview} (เว้นว่างเพื่อคงเดิม)` : 'วาง Bot Token จาก @BotFather'

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <header className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">การตั้งค่า</h1>
        <p className="text-sm text-muted-foreground mt-0.5">ตั้งค่าการแจ้งเตือนและระบบภายนอก</p>
      </header>

      <section className="border border-border rounded-[var(--radius)] bg-card shadow-[var(--shadow-sm)] overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">แจ้งเตือน Telegram</span>
          {!loading && data && (
            <span className={cn('ml-auto text-xs font-medium rounded-full px-2 py-0.5',
              data.enabled ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground')}>
              {data.enabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}
            </span>
          )}
        </div>

        {loading ? (
          <div className="p-4 space-y-4">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : error ? (
          <div className="p-6 flex flex-col items-center gap-3 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>ลองใหม่</Button>
          </div>
        ) : (
          <div className="p-4 space-y-5">
            <p className="text-xs text-muted-foreground leading-relaxed">
              แจ้งเตือนเข้ากลุ่ม Telegram เมื่อ <strong>ถึงเวลาออกผล</strong> (ปิดรับแทง + ถึงเวลาประกาศ)
              และเมื่อ <strong>แอดมินกรอกผล</strong> ระบบจะส่งเลขที่ออกให้อัตโนมัติ
            </p>

            <div className="flex items-center justify-between gap-4 py-1">
              <div>
                <Label className="text-sm">เปิดใช้งานการแจ้งเตือน</Label>
                <p className="text-xs text-muted-foreground mt-0.5">ต้องกรอก Bot Token และ Chat ID ก่อน</p>
              </div>
              <Toggle checked={enabled} onChange={setEnabled} label="เปิดใช้งานการแจ้งเตือน Telegram" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tg-token" className="text-sm">Bot Token</Label>
              <div className="relative">
                <Input
                  id="tg-token"
                  type={showToken ? 'text' : 'password'}
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder={tokenPlaceholder}
                  autoComplete="off"
                  className="pr-10 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showToken ? 'ซ่อน token' : 'แสดง token'}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tg-chat" className="text-sm">Chat ID (กลุ่ม)</Label>
              <Input
                id="tg-chat"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="เช่น -1001234567890"
                autoComplete="off"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">เพิ่มบอทเข้ากลุ่ม แล้วใช้ ID ของกลุ่ม (ขึ้นต้น -100 สำหรับซูเปอร์กรุ๊ป)</p>
            </div>

            {notice && (
              <div
                role="status"
                className={cn('flex items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-xs',
                  notice.kind === 'ok' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive')}
              >
                {notice.kind === 'ok' ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
                {notice.text}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={askToSave} disabled={saving || !dirty} className="gap-1.5">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                บันทึก
              </Button>
              <Button
                variant="outline"
                onClick={() => void test()}
                disabled={testing || !data?.hasToken || !data?.chatId}
                title={!data?.hasToken || !data?.chatId ? 'บันทึก Token และ Chat ID ก่อน' : 'ส่งข้อความทดสอบ'}
                className="gap-1.5"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                ทดสอบส่ง
              </Button>
            </div>
          </div>
        )}
      </section>

      <DrawAutoOpenCard />

      {pending && (
        <OtpConfirmDialog
          purpose="settings.telegram.update"
          payload={{ body: { ...pending } }}
          title="ยืนยันการตั้งค่าเทเลแกรม"
          description={`ปลายทางแจ้งเตือนจะเป็น ${pending.chatId || '(ว่าง)'}` +
            (pending.botToken ? ' และเปลี่ยน Bot Token ใหม่' : '') +
            ` · สถานะ: ${pending.enabled ? 'เปิดใช้งาน' : 'ปิด'}`}
          confirmLabel="บันทึกการตั้งค่า"
          onConfirm={(otp) => save(pending, otp)}
          onDone={() => setPending(null)}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  )
}
